import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectEventsData, setAnalyticsData, setEventDataList } from '../../store/events';
import { selectLoad, selectWebp } from '../../store/ui';
import { useLocation, useNavigate } from 'react-router-dom';
import useImagesSrc from '../../hooks/useImagesScr';
import { useSplitFlow, queryParser } from '@applyft-web/utils';
import { setLandingType } from '../../store/plans';
import { isAndroid, isDesktop, isIOS, osName, osVersion } from 'react-device-detect';
import { setAmplitudeUserProperties } from '../../analytics/amplitude';
import { EVENTS } from '../../analytics/events';
import { sendAnalyticsEvents } from '../../analytics/common';
import { useAnalyticsData } from '../../utils/checkoutHelper';
import { AppButton, AppDescription, AppHeadline } from '../../components';
import styled, { css } from 'styled-components';
import useWindowSize from '../../hooks/useWindowSize';
import { usePreloadImages } from '../../hooks/usePreloadImages';
import SignupScreen from '../signup/SignupScreen';
import { StyledBtn, StyledBtnWrapper } from '../checkout/CheckoutScreen';
import Q1 from '../questions/Q1';
import TriviaQ1 from '../trivia/Questions/Q1';

const StyledWelcomeImage = styled.div`
  background-image: ${({ imgPath }) => imgPath && `url('${imgPath}')`};
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
  display: flex;
  max-width: 375px;
  max-height: 345px;
  min-height: 150px;
  height: 100%;
  width: 100%;
  margin-top: 24px;
  @media screen and (max-width: 450px) {
    width: 100vw;
  }
  @media screen and (max-height: 700px) {
    margin-top: 5px;
  }
`;

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;

  ${({ $isIntro }) =>
    $isIntro &&
    css`
      justify-content: flex-start;
      margin-top: 20px;
    `}

  ${({ withDisclaimer }) =>
    withDisclaimer &&
    `
    @media screen and (max-width: 450px) {
      padding-bottom: 110px;
    }
  `}
`;

const StyledBtnWelcomeWrapper = styled(StyledBtnWrapper)`
  margin-top: 10px;
  color: rgb(175, 175, 175);
`;

const StyledDisclaimer = styled.div`
  font-size: 12px;
  color: rgb(175, 175, 175);
  text-align: center;
  padding: 15px 10px 0;
  max-width: 400px;

  span {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const Welcome = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [eventParams, setEventParams] = useState(null);
  const eventsData = useSelector(selectEventsData);
  const load = useSelector(selectLoad);
  const navigate = useNavigate();
  const location = useLocation();
  const analyticsParams = useAnalyticsData();
  const { imagesSources } = useImagesSrc();
  const windowSize = useWindowSize();
  const { innerHeight } = windowSize;
  const isWebp = useSelector(selectWebp);
  const landingParam = location.pathname.replace(/\//g, '');

  const { landingType, flowType: paywallType } = useSplitFlow(landingParam, {
    debug: process.env.REACT_APP_ENV !== 'production',
  });

  const isTriviaLanding = paywallType === 'trivia';
  const isRunwayerFlow = ['alfa_bravo'].includes(landingParam);
  const isDisplayLinks = ['fullPrice', 'trial_v2', 'trivia'].includes(paywallType);
  const isIntroOffer = landingParam === 'intro_offer';
  usePreloadImages(imagesSources['choose-target']);
  usePreloadImages('../assets/spinner-image.gif');

  useEffect(() => {
    if (eventParams || !paywallType) return;
    const { fbclid, ttclid, gclid, ...utms } = queryParser(location.search);
    const {
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
      Adset_name,
      Ad_name,
      CampaignID,
      AdSetID,
      AdID,
    } = utms;
    const facebookUtm = fbclid ? 'facebook' : null;
    const appDomain = `${window.location.origin}${landingParam.length ? '/' + landingParam : ''}`;
    const utmSource = utms['utm_source'] || facebookUtm || 'organic';
    utms['utm_source'] = utmSource;

    dispatch(setLandingType(paywallType));

    const localEventsData = {
      landing_type: `quiz_${landingType}`,
      app_domain: appDomain,
      app_name: 'Family-Locator',
      landingType,
      appDomain,
      appName: 'Family-Locator',
      fbclid,
      ttclid,
      gclid,
      utm: utms,
      utm_source: utmSource,
      os: `${osVersion} ${osName}`,
    };

    const userProperties = {
      AppDomain: appDomain,
      OSDevice: `${isDesktop ? 'desktop' : isIOS ? 'iOS' : isAndroid ? 'Android' : osName}`,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
      Adset_name,
      Ad_name,
      CampaignID,
      AdSetID,
      AdID,
    };

    dispatch(setEventDataList(localEventsData));
    setAmplitudeUserProperties(userProperties);
    setEventParams(localEventsData);
  }, [dispatch, eventParams, location, landingParam, landingType, paywallType]);

  useEffect(() => {
    if (!eventParams) return;
    sendAnalyticsEvents(EVENTS.sessionStart, eventParams);
    sendAnalyticsEvents(EVENTS.onboardingStarted, eventParams);
  }, [eventParams]);

  useEffect(() => {
    dispatch(setAnalyticsData(analyticsParams));
  }, [analyticsParams, dispatch]);

  const onContinueClick = () => {
    sendAnalyticsEvents(EVENTS.onboardingStartedSubmitted, eventsData);
    navigate(isTriviaLanding ? '/press' : '/choose-target');
  };
  const BottomLinks = () => {
    return (
      <>
        <StyledDisclaimer
          dangerouslySetInnerHTML={{ __html: t('welcome_disclaimer') }}
          onClick={(e) => {
            if (e.target.id === 'terms') {
              onLinkClick('https://family-locator.com/terms-of-use-info/');
            }
          }}
        />
        {isDisplayLinks && (
          <StyledBtnWelcomeWrapper>
            <StyledBtn
              id="link-to-terms"
              onClick={() => onLinkClick('https://family-locator.com/terms-of-use-info/')}
            >
              {t('terms')}
            </StyledBtn>
            <StyledBtn
              id="link-to-privacy"
              onClick={() => onLinkClick('https://family-locator.com/privacy-policy-info/')}
            >
              {t('privacy')}
            </StyledBtn>
          </StyledBtnWelcomeWrapper>
        )}
      </>
    );
  };
  const onLinkClick = (url) => window.open(url);
  if (isRunwayerFlow) {
    return <SignupScreen />;
  }
  if (!paywallType) {
    return null;
  }
  if (isTriviaLanding) {
    return (
      <>
        <TriviaQ1 />
        <BottomLinks />
      </>
    );
  }
  if (load) {
    if (isIntroOffer) {
      return (
        <MainWrapper $isIntro={isIntroOffer}>
          <Q1 />
        </MainWrapper>
      );
    }

    return (
      <MainWrapper withDisclaimer>
        <AppHeadline customStyles={'text-align: center; margin-bottom: 0px'}>
          {t('welcome')}
        </AppHeadline>
        <StyledWelcomeImage
          imgPath={isWebp ? '../assets/options/Welcome.webp' : '../assets/options/Welcome.png'}
        />
        <AppDescription
          customStyles={`font-size: 20px; line-height: 24px; margin-top: ${
            innerHeight <= 700 ? '-20px' : '10px'
          }; text-align: center`}
        >
          {t('welcome_sub')}
        </AppDescription>
        <AppButton
          customText={'lets_go_welcome'}
          onClick={onContinueClick}
          bottomPosition={isTriviaLanding ? '36px' : '16px'}
          customBeforeBg="none"
          bottomLinks={BottomLinks}
        />
      </MainWrapper>
    );
  }
  return <></>;
};

export default Welcome;
